import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import React from "react";
import { DeleteAssignmentModalController, DeleteAssignmentModalControllerDelegate } from "./controller";

const SUCCESS_CLOSE_DELAY_MS = 2500;

/** A ação é destrutiva: o ícone do título troca o azul do tema pelo vermelho. */
const destructiveTitleSx = {
  "& svg": {
    color: "error.main",
    backgroundColor: (theme: any) => alpha(theme.palette.error.light, 0.35),
  },
};

interface State {
  phase: "form" | "submitting" | "success";
  errorMessage: string;
}

interface Props {
  userUid: string;
  assignmentId: number;
  delegate: DeleteAssignmentModalDelegate;
}

export interface DeleteAssignmentModalDelegate {
  shouldCloseModal: () => void;
  applicationShouldLogout: () => void;
  didDeleteAssignment: () => void;
}

class DeleteAssignmentModal extends React.Component<Props, State> implements DeleteAssignmentModalControllerDelegate {

  controller: DeleteAssignmentModalController
  private closeTimer?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      phase: "form",
      errorMessage: "",
    }
    this.controller = new DeleteAssignmentModalController(this);
  }

  // delegate
  didDeleteAssignment() {
    this.setState({ phase: "success", errorMessage: "" });
    this.closeTimer = setTimeout(
      () => this.props.delegate.didDeleteAssignment(),
      SUCCESS_CLOSE_DELAY_MS
    );
  }

  didFailDeleteAssignment(message: string) {
    this.setState({ phase: "form", errorMessage: message });
  }

  applicationShouldLogout() {
    this.setState({ phase: "form" });
    this.props.delegate.applicationShouldLogout();
  };

  componentWillUnmount() {
    clearTimeout(this.closeTimer!);
  }

  // internals
  didClickDelete() {
    this.setState({ phase: "submitting", errorMessage: "" });
    const token = localStorage.getItem("AccessToken")!;
    this.controller.deleteAssignment(token, this.props.userUid, this.props.assignmentId)
  }

  didClickClose() {
    if (this.state.phase === "submitting") {
      return;
    }
    if (this.state.phase === "success") {
      clearTimeout(this.closeTimer!);
      this.props.delegate.didDeleteAssignment();
      return;
    }
    this.props.delegate.shouldCloseModal();
  }

  renderSubmitting() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={2}
        paddingY={5}
      >
        <CircularProgress color="error" />
        <Typography variant="body2" color="text.secondary">
          Removendo aplicação...
        </Typography>
      </Box>
    );
  }

  renderSuccess() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={1}
        paddingY={5}
      >
        <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
        <Typography variant="subtitle1" color="success.main" fontWeight="bold">
          Aplicação removida!
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          A aplicação não está mais associada a este usuário.
        </Typography>
      </Box>
    );
  }

  renderForm() {
    return (
      <Box display="flex" flexDirection="column" sx={{ gap: 2 }}>
        {this.state.errorMessage && (
          <Alert
            severity="error"
            onClose={() => this.setState({ errorMessage: "" })}
          >
            {this.state.errorMessage}
          </Alert>
        )}

        <Typography color="text.secondary">
          Tem certeza que deseja remover a aplicação desse usuário?
        </Typography>

        <Alert severity="warning">
          Isso irá remover todas as respostas do usuário até o momento. Essa
          ação é irreversível.
        </Alert>
      </Box>
    );
  }

  renderContent() {
    if (this.state.phase === "submitting") {
      return this.renderSubmitting();
    }
    if (this.state.phase === "success") {
      return this.renderSuccess();
    }
    return this.renderForm();
  }

  // Internals
  render() {
    return (
      <Dialog
        open
        onClose={() => this.didClickClose()}
        variant="modal"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle variant="modal" sx={destructiveTitleSx}>
          <DeleteForeverIcon />
          Remover aplicação
        </DialogTitle>
        <DialogContent variant="modal">{this.renderContent()}</DialogContent>
        {this.state.phase === "form" && (
          <DialogActions
            variant="modal"
            sx={{ flexWrap: "wrap", gap: 1, justifyContent: "end" }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => this.didClickClose()}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={() => this.didClickDelete()}
            >
              Remover Aplicação
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

export default DeleteAssignmentModal;
